import * as React from 'react'
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Grid,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography
} from '@mui/material'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import PublicRoundedIcon from '@mui/icons-material/PublicRounded'

import useAutoTranslate from '../hooks/useAutoTranslate'
import RiftCommanderRulesEditor from './RiftCommanderRulesEditor'
import RiftLiveDashboard from './RiftLiveDashboard'
import RiftShopOffersEditor from './RiftShopOffersEditor'
import RiftTroopReservesEditor from './RiftTroopReservesEditor'

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        minHeight: 42,
        borderRadius: '8px',
        backgroundColor: 'rgba(17, 29, 49, 0.72)'
    },
    '& .MuiInputBase-input, & .MuiSelect-select': {
        color: '#f5f7ff',
        fontWeight: 650
    }
}

const panelSx = {
    p: { xs: 2, md: 2.5 },
    borderRadius: '11px',
    border: '1px solid rgba(129, 146, 178, 0.24)',
    backgroundColor: 'rgba(20, 32, 53, 0.56)',
    boxShadow: 'none'
}

const parseCommanderCount = value => {
    const positions = new Set()
    String(value ?? '')
        .split(',')
        .map(part => part.trim())
        .filter(Boolean)
        .forEach(part => {
            if (!part.includes('-')) {
                const position = Number(part)
                if (Number.isFinite(position) && position >= 1 && position <= 350)
                    positions.add(position)
                return
            }

            const [rawStart, rawEnd] = part.split('-')
            const start = Number(rawStart)
            const end = Number(rawEnd)
            if (!Number.isFinite(start) || !Number.isFinite(end))
                return

            for (let position = Math.max(1, Math.min(start, end)); position <= Math.min(350, Math.max(start, end)); position += 1)
                positions.add(position)
        })

    return positions.size
}

function ModeButtons({ value, options, onChange }) {
    return <Stack direction="row" useFlexGap flexWrap="wrap" gap={0.8}>
        {options.map(([key, label]) => <Button key={key} variant="outlined" onClick={() => onChange(key)}
            aria-pressed={value === key} sx={{ ...questFilterButtonSx(value === key), minHeight: 34 }}>{label}</Button>)}
    </Stack>
}

function HelpText({ children }) {
    return (
        <Stack direction="row" spacing={0.7} alignItems="flex-start" sx={{ mt: 0.7 }}>
            <InfoOutlinedIcon sx={{ mt: '2px', fontSize: 14, color: '#7f93b5' }} />
            <Typography sx={{ color: '#7f93b5', fontSize: '0.79rem', lineHeight: 1.45 }}>
                {children}
            </Typography>
        </Stack>
    )
}

function SwitchSetting({ checked, onChange, label, helper, switchOnRight = false }) {
    return (
        <Box>
            <Stack direction="row" spacing={1.1} alignItems="center" justifyContent={switchOnRight ? 'space-between' : 'flex-start'}>
                {!switchOnRight && <Switch inputProps={{ 'aria-label': label }} checked={Boolean(checked)} onChange={event => onChange(event.target.checked)} />}
                <Typography sx={{ color: '#f5f7ff', fontSize: '0.93rem', fontWeight: 800 }}>
                    {label}
                </Typography>
                {switchOnRight && <Switch inputProps={{ 'aria-label': label }} checked={Boolean(checked)} onChange={event => onChange(event.target.checked)} />}
            </Stack>
            {helper ? <HelpText>{helper}</HelpText> : null}
        </Box>
    )
}

function LabeledField({ label, helper, children }) {
    return (
        <Box>
            <Typography sx={{ mb: 0.65, color: '#f5f7ff', fontSize: '0.82rem', fontWeight: 800 }}>
                {label}
            </Typography>
            {children}
            {helper ? <HelpText>{helper}</HelpText> : null}
        </Box>
    )
}

function PortalAccordion({ title, children, defaultExpanded = false }) {
    return (
        <Accordion
            defaultExpanded={defaultExpanded}
            disableGutters
            elevation={0}
            sx={{
                color: '#f5f7ff',
                backgroundColor: 'transparent',
                borderTop: '1px solid rgba(129, 146, 178, 0.13)',
                '&:before': { display: 'none' },
                '&.Mui-expanded': { m: 0 }
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreRoundedIcon sx={{ color: '#9eb0cd' }} />}
                sx={{ px: 0.3, minHeight: 52, '&.Mui-expanded': { minHeight: 52 } }}
            >
                <Typography sx={{ fontSize: '0.93rem', fontWeight: 850 }}>{title}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 0.3, pt: 0, pb: 2.2 }}>
                {children}
            </AccordionDetails>
        </Accordion>
    )
}

const questFilterButtonSx = selected => ({
    minHeight: 29,
    px: 1.15,
    py: 0.25,
    color: selected ? '#ffffff' : '#c2ccdc',
    borderColor: selected ? '#2f80ed' : 'rgba(129, 146, 178, 0.38)',
    backgroundColor: selected ? 'rgba(47, 128, 237, 0.22)' : 'rgba(17, 29, 49, 0.55)',
    textTransform: 'none',
    fontSize: '0.74rem',
    fontWeight: 800
})

export default function AllianceRiftPortalPanel({
    value,
    onChange,
    commanderWhiteList = '',
    onOpenCommanderAssignment,
    runtimeStatus,
    appLanguage = 'en'
}) {
    const t = useAutoTranslate(appLanguage, [
        'Live rift portal', 'Alliance Rift Portal', 'Attack The Portal', 'Rift commanders',
        'Every commander with the Rift tag gets its own rules. They are checked from the top down.',
        'No commander is set up for the portal', 'commander assigned to the portal', 'commanders assigned to the portal',
        'Assign commanders to the rift portal in Commander Allocation.', 'Open commander assignment', 'Commanders',
        'The master switch for sending attacks. Switch it off to pause new attacks while the other bot functions keep running.'
    ])

    const settings = value && typeof value === 'object' ? value : {}
    const commanderCount = parseCommanderCount(commanderWhiteList)
    const updateValue = (key, nextValue) => onChange?.({ ...settings, [key]: nextValue })
    const liveMessage = String(runtimeStatus?.message ?? runtimeStatus?.statusText ?? '').trim()
    return (
        <Stack spacing={2.1}>
            <Paper sx={panelSx}>
                <Stack spacing={1.7}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.2}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <PublicRoundedIcon sx={{ color: '#8fb4ff', fontSize: 21 }} />
                            <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 850 }}>
                                {t('Live rift portal')}
                            </Typography>
                        </Stack>
                    </Stack>
                    <RiftLiveDashboard status={runtimeStatus ? { ...runtimeStatus, message: liveMessage } : undefined} />
                </Stack>
            </Paper>

            <Paper sx={panelSx}>
                <Stack spacing={2.2}>
                    <Box>
                        <Typography sx={{ color: '#f5f7ff', fontSize: '1rem', fontWeight: 850 }}>
                            {t('Alliance Rift Portal')}
                        </Typography>
                        <Typography sx={{ mt: 0.45, color: '#79a7ef', fontSize: '0.8rem', lineHeight: 1.45 }}>
                            Give every rift commander its own rules for when it attacks the portal and when an attack is called back.
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            px: 1.45,
                            py: 1.05,
                            borderRadius: '8px',
                            border: '1px solid rgba(129, 146, 178, 0.25)',
                            backgroundColor: 'rgba(14, 25, 44, 0.42)'
                        }}
                    >
                        <SwitchSetting
                            checked={settings.attackPortal}
                            onChange={nextValue => updateValue('attackPortal', nextValue)}
                            label={t('Attack The Portal')}
                            helper={t('The master switch for sending attacks. Switch it off to pause new attacks while the other bot functions keep running.')}
                        />
                    </Box>

                    <Box>
                        <Typography sx={{ color: '#f5f7ff', fontSize: '0.85rem', fontWeight: 850 }}>
                            {t('Rift commanders')}
                        </Typography>
                        <Typography sx={{ mt: 0.5, color: '#7f93b5', fontSize: '0.79rem', lineHeight: 1.45 }}>
                            {t('Every commander with the Rift tag gets its own rules. They are checked from the top down.')}
                        </Typography>
                        <Box
                            sx={{
                                mt: 1.25,
                                minHeight: 126,
                                px: 2,
                                py: 1.8,
                                display: 'grid',
                                placeItems: 'center',
                                textAlign: 'center',
                                borderRadius: '8px',
                                border: '1px dashed rgba(129, 146, 178, 0.34)',
                                backgroundColor: 'rgba(14, 25, 44, 0.25)'
                            }}
                        >
                            <Stack spacing={1} alignItems="center">
                                <GroupsRoundedIcon sx={{ color: commanderCount ? '#8fb4ff' : '#7f93b5' }} />
                                <Typography sx={{ color: '#f5f7ff', fontSize: '0.83rem', fontWeight: 850 }}>
                                    {commanderCount ?
                                        `${commanderCount} ${t(commanderCount === 1 ? 'commander assigned to the portal' : 'commanders assigned to the portal')}` :
                                        t('No commander is set up for the portal')}
                                </Typography>
                                <Typography sx={{ color: '#6f8bb7', fontSize: '0.75rem' }}>
                                    {t('Assign commanders to the rift portal in Commander Allocation.')}
                                </Typography>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<GroupsRoundedIcon />}
                                    onClick={onOpenCommanderAssignment}
                                    sx={{
                                        minHeight: 34,
                                        px: 1.6,
                                        borderRadius: '7px',
                                        color: '#f5f7ff',
                                        borderColor: 'rgba(129, 146, 178, 0.44)',
                                        textTransform: 'none',
                                        fontWeight: 750
                                    }}
                                >
                                    {t('Open commander assignment')}
                                </Button>
                            </Stack>
                        </Box>
                    </Box>

                    {commanderCount > 0 && <RiftCommanderRulesEditor
                        commanderWhiteList={commanderWhiteList} commanderRules={settings.commanderRules}
                        ruleSets={settings.ruleSets} onChange={patch => onChange?.({ ...settings, ...patch })} />}
                    <HelpText>Only commanders with the rift tag are used. The all tag does not qualify. Rules are checked from top to bottom; a commander without rules is never sent.</HelpText>
                    <RiftTroopReservesEditor value={settings.troopReserves || []} onChange={next => updateValue('troopReserves', next)} appLanguage={appLanguage} />
                    <LabeledField label="Travel Mode" helper={settings.useTravelFeathers || settings.travelMode === 'fast_travel_feathers' ?
                        'Attacks use fast travel feathers.' : settings.travelMode === 'no_horse' ?
                        'Attacks walk to the portal. No travel boost is purchased.' : 'Use the best unlocked coin horse the sending castle can afford.'}>
                        <ModeButtons value={settings.useTravelFeathers ? 'fast_travel_feathers' : settings.travelMode || 'fastest_coin_horse'}
                            options={[['no_horse', 'No horse'], ['fastest_coin_horse', 'Coin horse'], ['fast_travel_feathers', 'Fast travel feathers']]}
                            onChange={next => onChange?.({ ...settings, travelMode: next, useTravelFeathers: next === 'fast_travel_feathers' })} />
                    </LabeledField>
                    <LabeledField label="Boss eligibility stop" helper="Pause at your points on the current boss level. The reward minimum follows each new level. Either this stop or the activity stop can pause attacks.">
                        <ModeButtons value={settings.stopAttackingMode || 'own_points'} onChange={next => updateValue('stopAttackingMode', next)}
                            options={[['never', 'Off'], ['own_points', 'Stop at my own number'], ['boss_points', 'Stop at the boss reward minimum']]} />
                    </LabeledField>
                    {(settings.stopAttackingMode || 'own_points') === 'own_points' && <LabeledField label="Stop At This Many Points On The Boss Level" helper="Set 0 to keep attacking without a boss points limit.">
                        <TextField fullWidth type="number" value={settings.stopAtBossPoints || '0'} onChange={event => updateValue('stopAtBossPoints', event.target.value)} inputProps={{ min: 0 }} sx={fieldSx} />
                    </LabeledField>}
                    <SwitchSetting checked={settings.callAllAttacksBackWhenStopping} onChange={next => updateValue('callAllAttacksBackWhenStopping', next)}
                        label="Call All Attacks Back When Stopping" helper="At a points stop, recall attacks still inside the game's 10-minute recall window. Otherwise they continue flying and their retreat rules remain active." />
                    <LabeledField label="Activity points stop" helper="Stop at your personal event total. Activity points remain valid when the boss levels up.">
                        <ModeButtons value={settings.activityStopMode || 'off'} onChange={next => updateValue('activityStopMode', next)}
                            options={[['off', 'Off'], ['all_rewards', 'All activity rewards'], ['own_points', 'My point target']]} />
                    </LabeledField>
                    {settings.activityStopMode === 'own_points' && <LabeledField label="Activity points target" helper="Your personal event total at which to stop, for example 700000. Set 0 for no activity limit.">
                        <TextField fullWidth type="number" value={settings.stopAtActivityPoints || '0'} onChange={event => updateValue('stopAtActivityPoints', event.target.value)} inputProps={{ min: 0 }} sx={fieldSx} />
                    </LabeledField>}
                    <Box sx={{ p: 1.4, border: '1px solid rgba(129,146,178,.24)', borderRadius: 2 }}>
                        <SwitchSetting switchOnRight checked={settings.shopEnabled} onChange={next => onChange?.({ ...settings, shopEnabled: next, shopPurchaseLimit: next && settings.shopOffers?.length ? '999999999' : '0' })} label="Buy From The Rift Shop" helper="Buy the selected offers below whenever the Rift event shop is open." />
                    </Box>
                    {settings.shopEnabled && <RiftShopOffersEditor offers={settings.shopOffers} onChange={next => onChange?.({ ...settings, shopOffers: next, shopPurchaseLimit: next.length ? '999999999' : '0' })} />}
                    <Box>
                        <PortalAccordion title={t('Commanders')} defaultExpanded>
                            <SwitchSetting checked={settings.requireShieldMaidenSupport} onChange={next => updateValue('requireShieldMaidenSupport', next)}
                                label="Require Shield-maiden Support" helper="Only use commanders that add Shield-maiden support units in the attack courtyard." />
                        </PortalAccordion>

                        <PortalAccordion title="Call-back checks" defaultExpanded>
                            <Stack spacing={2}>
                                <HelpText>Choose when to inspect watched walls and apply each running attack's retreat rule.</HelpText>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}><LabeledField label="Wall Check Before Impact (Seconds)" helper="Monitor once per second in this interval until the attack lands or is recalled.">
                                        <TextField fullWidth type="number" value={settings.wallCheckBeforeImpactSeconds || '15'} onChange={event => updateValue('wallCheckBeforeImpactSeconds', event.target.value)} inputProps={{ min: 1, max: 300 }} sx={fieldSx} />
                                    </LabeledField></Grid>
                                    <Grid item xs={12} md={6}><SwitchSetting checked={settings.wallCheckAtCallbackWindow} onChange={next => updateValue('wallCheckAtCallbackWindow', next)} label="Wall Check At The Call-Back Window"
                                        helper="For journeys over 10 minutes, check once about 5 seconds before the recall window closes. Apply the retreat rule then; otherwise the attack keeps flying. Short journeys use the check before impact." /></Grid>
                                </Grid>
                                <SwitchSetting checked={settings.onlySendRecallableAttacks} onChange={next => updateValue('onlySendRecallableAttacks', next)} label="Only Send Attacks That Can Be Called Back"
                                    helper="Estimate travel before sending. If it exceeds 10 minutes or cannot be estimated, wait and retry after the attack cooldown." />
                            </Stack>
                        </PortalAccordion>

                    </Box>
                </Stack>
            </Paper>
        </Stack>
    )
}
